import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { apiGet, apiPatch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Banknote, Clock, CheckCircle, XCircle, Eye, Download, Calendar, ChevronDown, Copy, Loader2, BarChart3 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import * as XLSX from "xlsx";

interface WithdrawalRequest {
  id: string;
  user_id: string;
  amount: number;
  pix_key: string;
  cnpj: string;
  status: "pending" | "completed" | "rejected";
  nutrition_amount: number;
  weller_amount: number;
  nutrition_paid: boolean;
  weller_paid: boolean;
  created_at: string;
  updated_at?: string;
  influencers: {
    name: string;
    email: string;
    coupon: string;
  };
}

interface AdminStats {
  total_liability: number;
  available_in_wallets: number;
  pending_requests: number;
  total_paid: number;
  nutrition_pending: number;
  weller_pending: number;
  nutrition_wallet: number;
  weller_wallet: number;
  nutrition_paid: number;
  weller_paid: number;
}

const PT_MONTHS: Record<string, string> = {
  "01": "Janeiro", "02": "Fevereiro", "03": "Março", "04": "Abril",
  "05": "Maio", "06": "Junho", "07": "Julho", "08": "Agosto",
  "09": "Setembro", "10": "Outubro", "11": "Novembro", "12": "Dezembro",
};

export const AdminWithdrawalRequests: React.FC = () => {
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(new Set());
  const [detailRequest, setDetailRequest] = useState<WithdrawalRequest | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    requestId: string;
    type: "nutrition" | "weller" | "cancel";
    request: WithdrawalRequest;
  } | null>(null);

  // Always fetch ALL requests once, filter client-side
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [reqRes, statsRes] = await Promise.all([
          apiGet("/api/withdrawals/admin"),
          apiGet("/api/withdrawals/admin-stats"),
        ]);
        if (reqRes.ok) setRequests(await reqRes.json());
        if (statsRes.ok) setStats(await statsRes.json());
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const refreshData = async () => {
    try {
      const [reqRes, statsRes] = await Promise.all([
        apiGet("/api/withdrawals/admin"),
        apiGet("/api/withdrawals/admin-stats"),
      ]);
      if (reqRes.ok) setRequests(await reqRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (error) {
      console.error("Error refreshing:", error);
    }
  };

  const exportToExcel = () => {
    if (!requests.length) { toast.error("Não há dados para exportar"); return; }
    const statusMap: Record<string, string> = { pending: "Pendente", completed: "Pago", rejected: "Cancelado" };
    const excelData = requests.map(req => ({
      "Influenciador": req.influencers?.name || "",
      "Cupom": req.influencers?.coupon || "",
      "Valor Total (R$)": req.amount,
      "Nutrition (R$)": req.nutrition_amount || 0,
      "Nutrition Pago": req.nutrition_paid ? "Sim" : "Não",
      "Veller/TK (R$)": req.weller_amount || 0,
      "Veller/TK Pago": req.weller_paid ? "Sim" : "Não",
      "Status": statusMap[req.status] || req.status,
      "PIX": req.pix_key,
      "CPF/CNPJ": req.cnpj,
      "Data Solicitação": format(new Date(req.created_at), "dd/MM/yyyy HH:mm"),
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    ws["!cols"] = [{ wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 30 }, { wch: 18 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, ws, "Saques");
    XLSX.writeFile(wb, `saques_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success("Exportação concluída!");
  };

  const updateRequestStatus = async (requestId: string, status: "pending" | "completed" | "rejected") => {
    setUpdating(requestId);
    try {
      const response = await apiPatch("/api/withdrawals/admin", { id: requestId, status });
      if (response.ok) {
        const updated = await response.json();
        setRequests(requests.map((r) => (r.id === requestId ? { ...r, ...updated } : r)));
        toast.success(`Status: ${status === "completed" ? "Pago" : status === "rejected" ? "Cancelado" : "Pendente"}`);
        refreshData();
      } else {
        const err = await response.json();
        toast.error(err.error || "Erro");
      }
    } catch { toast.error("Erro ao atualizar"); } finally { setUpdating(null); }
  };

  const updateCompanyPaid = async (requestId: string, company: "nutrition" | "weller") => {
    setUpdating(requestId);
    try {
      const body: Record<string, unknown> = { id: requestId };
      body[company === "nutrition" ? "nutrition_paid" : "weller_paid"] = true;
      const response = await apiPatch("/api/withdrawals/admin", body);
      if (response.ok) {
        const updated = await response.json();
        setRequests(requests.map((r) => (r.id === requestId ? { ...r, ...updated } : r)));
        toast.success(`${company === "nutrition" ? "Nutrition" : "Veller/TK"} pago`);
        refreshData();
      } else {
        const err = await response.json();
        toast.error(err.error || "Erro");
      }
    } catch { toast.error("Erro"); } finally { setUpdating(null); }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado!");
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  const formatDateShort = (dateString: string) =>
    format(new Date(dateString), "dd/MM 'às' HH:mm", { locale: ptBR });

  // Counts from ALL requests (not filtered)
  const statusCounts = useMemo(() => {
    const c = { pending: 0, completed: 0, rejected: 0, total: 0 };
    requests.forEach((r) => { c[r.status as keyof typeof c]++; c.total++; });
    return c;
  }, [requests]);

  const filteredRequests = useMemo(() => {
    return requests.filter((r) => r.status === statusFilter);
  }, [requests, statusFilter]);

  const groupByMonth = (reqs: WithdrawalRequest[]) => {
    const groups: Record<string, WithdrawalRequest[]> = {};
    for (const req of reqs) {
      const d = new Date(req.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(req);
    }
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  };

  const grouped = useMemo(() => groupByMonth(filteredRequests), [filteredRequests]);

  const toggleMonth = (key: string) => {
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const renderGroupedTable = (groups: [string, WithdrawalRequest[]][], emptyMsg: string, defaultCollapsed = false) => (
    <Card>
      <CardContent className="p-0">
        {groups.length > 0 ? (
          <div>
            {groups.map(([monthKey, monthReqs]) => {
              const [year, month] = monthKey.split("-");
              const monthTotal = monthReqs.reduce((s, r) => s + r.amount, 0);
              const monthPending = monthReqs.filter((r) => r.status === "pending").length;
              const isCollapsed = defaultCollapsed ? !collapsedMonths.has(monthKey) : collapsedMonths.has(monthKey);

              return (
                <div key={monthKey}>
                  <button
                    onClick={() => toggleMonth(monthKey)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.02] border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm font-semibold">{PT_MONTHS[month]} {year}</span>
                      <span className="text-xs text-muted-foreground">({monthReqs.length})</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-muted-foreground">{formatCurrency(monthTotal)}</span>
                      {monthPending > 0 && <span className="text-yellow-400 font-medium">{monthPending} pendente{monthPending > 1 ? "s" : ""}</span>}
                    </div>
                  </button>
                  {!isCollapsed && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/5 text-xs text-muted-foreground">
                            <th className="text-left px-4 py-2 font-medium">Influenciador</th>
                            <th className="text-right px-3 py-2 font-medium">Total</th>
                            <th className="text-right px-3 py-2 font-medium text-orange-400">Nutrition</th>
                            <th className="text-right px-3 py-2 font-medium text-blue-400">Veller/TK</th>
                            <th className="text-center px-3 py-2 font-medium">Status</th>
                            <th className="text-left px-3 py-2 font-medium">Data</th>
                            <th className="text-right px-4 py-2 font-medium">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthReqs.map((req) => (
                            <tr key={req.id} className="border-b border-white/[0.03] hover:bg-white/[0.03] transition-colors">
                              <td className="px-4 py-3">
                                <p className="font-medium text-sm">{req.influencers?.name || "—"}</p>
                                <p className="text-xs text-muted-foreground font-mono">{req.influencers?.coupon}</p>
                              </td>
                              <td className="text-right px-3 py-3 font-semibold tabular-nums">{formatCurrency(req.amount)}</td>
                              <td className="text-right px-3 py-3 tabular-nums">
                                {req.nutrition_amount > 0 ? (
                                  <span className={req.nutrition_paid ? "text-green-400" : "text-orange-300"}>
                                    {formatCurrency(req.nutrition_amount)}{req.nutrition_paid && " ✓"}
                                  </span>
                                ) : <span className="text-muted-foreground/30">—</span>}
                              </td>
                              <td className="text-right px-3 py-3 tabular-nums">
                                {req.weller_amount > 0 ? (
                                  <span className={req.weller_paid ? "text-green-400" : "text-blue-300"}>
                                    {formatCurrency(req.weller_amount)}{req.weller_paid && " ✓"}
                                  </span>
                                ) : <span className="text-muted-foreground/30">—</span>}
                              </td>
                              <td className="text-center px-3 py-3"><StatusBadge status={req.status} /></td>
                              <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDateShort(req.created_at)}</td>
                              <td className="text-right px-4 py-3">
                                <div className="flex items-center justify-end gap-1">
                                  {req.status === "pending" && (
                                    <>
                                      {req.nutrition_amount > 0 && !req.nutrition_paid && (
                                        <Button size="sm" onClick={() => setConfirmAction({ requestId: req.id, type: "nutrition", request: req })} disabled={updating === req.id} className="bg-orange-600/80 hover:bg-orange-600 text-xs h-7 px-2">
                                          {updating === req.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Pagar Nutrition"}
                                        </Button>
                                      )}
                                      {req.weller_amount > 0 && !req.weller_paid && (
                                        <Button size="sm" onClick={() => setConfirmAction({ requestId: req.id, type: "weller", request: req })} disabled={updating === req.id} className="bg-blue-600/80 hover:bg-blue-600 text-xs h-7 px-2">
                                          {updating === req.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Pagar Veller/TK"}
                                        </Button>
                                      )}
                                    </>
                                  )}
                                  <Button variant="ghost" size="sm" onClick={() => setDetailRequest(req)} className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground">
                                    <Eye className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : emptyMsg ? (
          <div className="text-center py-12">
            <Banknote className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">{emptyMsg}</p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );

  const StatusBadge = ({ status }: { status: string }) => {
    if (status === "pending") return <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 text-xs"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
    if (status === "completed") return <Badge variant="secondary" className="bg-green-500/20 text-green-300 border border-green-500/30 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Pago</Badge>;
    if (status === "rejected") return <Badge variant="secondary" className="bg-red-500/20 text-red-300 border border-red-500/30 text-xs"><XCircle className="w-3 h-3 mr-1" />Cancelado</Badge>;
    return <Badge variant="secondary" className="text-xs">{status}</Badge>;
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="h-4 w-24 bg-muted rounded animate-pulse mb-3" />
                <div className="h-8 w-32 bg-muted rounded animate-pulse mb-2" />
                <div className="h-3 w-20 bg-muted rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="h-10 w-full bg-muted rounded animate-pulse" />
        <Card>
          <CardContent className="pt-6 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 w-full bg-muted rounded animate-pulse" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards — grouped by company */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Nutrition group */}
        <div className="rounded-2xl border border-orange-500/15 bg-white/[0.03] backdrop-blur-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-orange-400" />
            <span className="text-xs font-semibold text-orange-400 uppercase tracking-wider">Nutrition</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Carteiras</p>
              <p className="text-sm sm:text-lg font-bold">{stats ? formatCurrency(stats.nutrition_wallet) : "..."}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Pendente</p>
              <p className="text-sm sm:text-lg font-bold text-yellow-400">{stats ? formatCurrency(stats.nutrition_pending) : "..."}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Já Pago</p>
              <p className="text-sm sm:text-lg font-bold text-green-400">{stats ? formatCurrency(stats.nutrition_paid) : "..."}</p>
            </div>
          </div>
        </div>

        {/* Veller/TK group */}
        <div className="rounded-2xl border border-blue-500/15 bg-white/[0.03] backdrop-blur-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-blue-400" />
            <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Veller/TK</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Carteiras</p>
              <p className="text-sm sm:text-lg font-bold">{stats ? formatCurrency(stats.weller_wallet) : "..."}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Pendente</p>
              <p className="text-sm sm:text-lg font-bold text-yellow-400">{stats ? formatCurrency(stats.weller_pending) : "..."}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Já Pago</p>
              <p className="text-sm sm:text-lg font-bold text-green-400">{stats ? formatCurrency(stats.weller_paid) : "..."}</p>
            </div>
          </div>
        </div>

        {/* Combined totals */}
        <div className="col-span-2 lg:col-span-1 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Totais</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Nas carteiras</p>
              <p className="text-sm sm:text-lg font-bold">{stats ? formatCurrency(stats.available_in_wallets) : "..."}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total pago</p>
              <p className="text-sm sm:text-lg font-bold text-green-400">{stats ? formatCurrency(stats.total_paid) : "..."}</p>
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Nutrition paga comissões de vendas até Jan/26. Veller/TK paga comissões a partir de Fev/26.
      </p>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Banknote className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold">Solicitações</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full border border-white/10 overflow-hidden">
            <button
              onClick={() => setStatusFilter("pending")}
              className={`px-4 py-2 text-xs font-medium transition-all flex items-center gap-1.5 ${statusFilter === "pending" ? "bg-yellow-600 text-white shadow-lg" : "bg-white/5 hover:bg-white/10 text-muted-foreground"}`}
            >
              <Clock className="h-3 w-3" />
              Pendentes ({statusCounts.pending})
            </button>
            <button
              onClick={() => setStatusFilter("completed")}
              className={`px-4 py-2 text-xs font-medium transition-all flex items-center gap-1.5 ${statusFilter === "completed" ? "bg-green-600 text-white shadow-lg" : "bg-white/5 hover:bg-white/10 text-muted-foreground"}`}
            >
              <CheckCircle className="h-3 w-3" />
              Pagos ({statusCounts.completed})
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={exportToExcel} className="border-white/10 bg-white/5 hover:bg-white/10 rounded-full">
            <Download className="w-4 h-4 mr-2" />Exportar Excel (.xlsx)
          </Button>
        </div>
      </div>

      {/* Requests Table — grouped by month */}
      {renderGroupedTable(grouped, "Nenhuma solicitação pendente")}

      {/* Detail Dialog — includes Cancel action and PIX copy */}
      <Dialog open={!!detailRequest} onOpenChange={(open) => !open && setDetailRequest(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalhes da Solicitação</DialogTitle>
          </DialogHeader>
          {detailRequest && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">{detailRequest.influencers?.name}</p>
                  <p className="text-sm text-muted-foreground">{detailRequest.influencers?.email}</p>
                </div>
                <StatusBadge status={detailRequest.status} />
              </div>

              <div className="grid grid-cols-3 gap-3 p-3 rounded-lg bg-white/5">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Total</p>
                  <p className="font-bold">{formatCurrency(detailRequest.amount)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-orange-400 mb-1">Nutrition</p>
                  <p className={`font-bold ${detailRequest.nutrition_paid ? "text-green-400" : "text-orange-300"}`}>
                    {detailRequest.nutrition_amount > 0 ? formatCurrency(detailRequest.nutrition_amount) : "—"}
                    {detailRequest.nutrition_paid && " ✓"}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-blue-400 mb-1">Veller/TK</p>
                  <p className={`font-bold ${detailRequest.weller_paid ? "text-green-400" : "text-blue-300"}`}>
                    {detailRequest.weller_amount > 0 ? formatCurrency(detailRequest.weller_amount) : "—"}
                    {detailRequest.weller_paid && " ✓"}
                  </p>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between p-2 rounded bg-white/5">
                  <div>
                    <p className="text-xs text-muted-foreground">Chave PIX</p>
                    <p className="font-mono text-xs">{detailRequest.pix_key}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(detailRequest.pix_key)} className="h-7 w-7 p-0">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-white/5">
                  <div>
                    <p className="text-xs text-muted-foreground">CPF/CNPJ</p>
                    <p className="font-mono text-xs">{detailRequest.cnpj}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(detailRequest.cnpj)} className="h-7 w-7 p-0">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="p-2 rounded bg-white/5">
                  <p className="text-xs text-muted-foreground">Cupom</p>
                  <p className="font-mono text-xs font-bold uppercase">{detailRequest.influencers?.coupon}</p>
                </div>
                <div className="p-2 rounded bg-white/5">
                  <p className="text-xs text-muted-foreground">Solicitado em</p>
                  <p className="text-xs">{format(new Date(detailRequest.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
                </div>
              </div>

              {detailRequest.status === "pending" && (
                <DialogFooter>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setDetailRequest(null);
                      setConfirmAction({ requestId: detailRequest.id, type: "cancel", request: detailRequest });
                    }}
                    className="text-xs"
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" />
                    Cancelar Solicitação
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === "cancel" ? "Cancelar solicitação?" : "Confirmar pagamento"}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.type === "cancel" ? (
                <>
                  A solicitação de <strong>{confirmAction?.request && formatCurrency(confirmAction.request.amount)}</strong> de{" "}
                  <strong>{confirmAction?.request?.influencers?.name}</strong> será cancelada. O saldo será devolvido à carteira.
                </>
              ) : confirmAction?.type === "nutrition" ? (
                <>
                  Confirma pagamento de <strong>{confirmAction?.request && formatCurrency(confirmAction.request.nutrition_amount)}</strong> (Nutrition) para{" "}
                  <strong>{confirmAction?.request?.influencers?.name}</strong>?
                </>
              ) : (
                <>
                  Confirma pagamento de <strong>{confirmAction?.request && formatCurrency(confirmAction.request.weller_amount)}</strong> (Veller/TK) para{" "}
                  <strong>{confirmAction?.request?.influencers?.name}</strong>?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="border-white/10 bg-white/5 hover:bg-white/10">
              Voltar
            </Button>
            <Button
              onClick={async () => {
                if (!confirmAction) return;
                if (confirmAction.type === "cancel") await updateRequestStatus(confirmAction.requestId, "rejected");
                else await updateCompanyPaid(confirmAction.requestId, confirmAction.type);
                setConfirmAction(null);
              }}
              disabled={updating === confirmAction?.requestId}
              className={
                confirmAction?.type === "cancel" ? "bg-red-600 hover:bg-red-700"
                  : confirmAction?.type === "nutrition" ? "bg-orange-600 hover:bg-orange-700"
                    : "bg-blue-600 hover:bg-blue-700"
              }
            >
              {updating === confirmAction?.requestId ? <Loader2 className="h-4 w-4 animate-spin" />
                : confirmAction?.type === "cancel" ? "Sim, cancelar" : "Confirmar pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
